import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { getApolloClient } from '../components/providers/graphql';
import {
  CheckZdProviderForMatchDocument,
  ClearAssessmentResponseDocument,
  ClearAssessmentResponseInput,
  DocumentInput,
  EditPatientEmergencyContactDocument,
  EditPatientGuardiansDocument,
  EditPatientGuardiansInput,
  EditPatientPharmacyDocument,
  EditPatientPrimaryPolicyholderDocument,
  GetDocumentStatusDocument,
  GetDocumentStatusQuery,
  GetProviderMatchesDocument,
  PatientAssignProviderMatchDocument,
  PatientEmergencyContactInput,
  PatientInput,
  PatientPharmacyInput,
  PatientPrimaryPolicyholderInput,
  UpdateAssessmentAnswersDocument,
  UpdateAssessmentAnswersInput,
  UpdateDocumentDocument,
  UpdateGoogleClickIdDocument,
  UpdatePatientConsentReferrerSharingDocument,
  UpdatePatientDemographicsDocument,
} from '../generated/graphql';
import { logClientDataPoint } from '../helpers/client-diagnostics';

export const updateDocument = async (
  input: DocumentInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: UpdateDocumentDocument,
    variables: { input },
  });

export const updatePatientDemographics = async (
  patient: PatientInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: UpdatePatientDemographicsDocument,
    variables: { input: patient },
  });

export const editPatientGuardians = async (
  input: EditPatientGuardiansInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: EditPatientGuardiansDocument,
    variables: { input },
  });

export const updateAssessmentAnswers = async (
  input: UpdateAssessmentAnswersInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: UpdateAssessmentAnswersDocument,
    variables: { input },
  });

export const fetchMatches = async (
  sessionPatientId: string
): Promise<FetchResult> => {
  const response = await getApolloClient().query({
    query: GetProviderMatchesDocument,
  });

  if (response.errors || response.error) {
    logClientDataPoint(
      'patient_provider_match_error',
      { sessionPatientId },
      response.error?.message ||
        response.errors?.map(err => err.message).join(',')
    );
  }
  return response;
};

export const zdCheckProviderMatch = async (): Promise<FetchResult> => {
  const response = await getApolloClient().query({
    query: CheckZdProviderForMatchDocument,
  });

  return response;
};

export const patientAssignProviderMatch = async (
  sessionPatientId: string,
  providerId?: string
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: PatientAssignProviderMatchDocument,
    variables: { sessionPatientId, providerId },
  });

export const editPrimaryPolicyholder = async (
  input?: PatientPrimaryPolicyholderInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: EditPatientPrimaryPolicyholderDocument,
    variables: { input },
  });

export const editPatientPharmacy = async (
  input?: PatientPharmacyInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: EditPatientPharmacyDocument,
    variables: { input },
  });

export const editEmergencyContact = async (
  input: PatientEmergencyContactInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: EditPatientEmergencyContactDocument,
    variables: { input },
  });

export const clearAssessmentResponse = async (
  input: ClearAssessmentResponseInput
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: ClearAssessmentResponseDocument,
    variables: { input },
  });

export const getDocumentStatus = async (): Promise<
  ApolloQueryResult<GetDocumentStatusQuery>
> =>
  getApolloClient().query<GetDocumentStatusQuery>({
    query: GetDocumentStatusDocument,
  });

export const updatePatientConsentReferrerSharing = async (
  consentReferrerSharing: boolean
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: UpdatePatientConsentReferrerSharingDocument,
    variables: { consentReferrerSharing },
  });

export const updateGoogleClickId = async (
  googleClickId: string
): Promise<FetchResult> =>
  getApolloClient().mutate({
    mutation: UpdateGoogleClickIdDocument,
    variables: { googleClickId },
  });
